/**
 * The dashboard module's service — every API call the dashboard makes passes through here.
 *
 * ---------------------------------------------------------------------------
 * IT PERFORMS NO RENDERING AND IMPORTS NO REACT
 * ---------------------------------------------------------------------------
 * The service's job is to read and to COUNT. Counting is done HERE rather than in a component
 * so that the same number is produced everywhere it appears, and so that a count can be tested
 * without rendering anything.
 *
 * ---------------------------------------------------------------------------
 * A COUNT IS NEVER INVENTED
 * ---------------------------------------------------------------------------
 * Every number this file returns is COUNTED FROM A READ. Nothing is estimated, nothing is
 * carried over from a previous session, and nothing is defaulted to zero because a read failed —
 * a failed read THROWS, and the widget shows the error state. A dashboard that shows "0" for
 * something it could not read is worse than one that says it does not know.
 *
 * The one exception is stated openly: the LOW-STOCK THRESHOLD is the console's own policy, and
 * the widget PRINTS it, so the reader can see which rule produced the number.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPES LIVE IN `summaries.ts`
 * ---------------------------------------------------------------------------
 * What a read produces once it has been counted is a value, not a read, and it lives beside the
 * counting rules that decide what it means. This file is the reads.
 *
 * A read that carries its OWN counting rule lives beside this file — `offers.ts` for the offer
 * read, `stock.ts` for the stock read and the low-stock threshold — so the rule and the read it
 * applies to cannot drift apart.
 */
import { http } from '@/api/client/http';
import { CanonicalProductListResponseSchema } from '@/api/schemas/catalog';
import { EntitlementsResponseSchema } from '@/api/schemas/foundation';
import { StockMovementListResponseSchema } from '@/api/schemas/inventory';
import { MediaAssetListResponseSchema } from '@/api/schemas/media';
import { TenantListResponseSchema } from '@/api/schemas/tenants';
import {
  OnboardingReadinessResponseSchema,
  OperationsBacklogResponseSchema,
  StaffMembershipListResponseSchema,
  VendorOperationsSummaryResponseSchema,
} from '@/api/schemas/dashboard';
import { readOffers } from '@/modules/dashboard/services/offers';
import { readStock } from '@/modules/dashboard/services/stock';
import {
  OPERATIONS_PURPOSE,
  emptyOfferStates,
  parseQuantity,
  type CatalogueSummary,
  type EntitlementSummary,
  type MediaSummary,
  type OfferStateCounts,
  type OffersSummary,
  type RecentActivitySummary,
  type StockOptions,
  type StockSummary,
  type TeamSummary,
  type TenantSummary,
} from '@/modules/dashboard/services/summaries';

/* The counting rules are re-exported here so the module's public surface has ONE place to
 * import them from. The SUMMARY TYPES are re-exported from `summaries.ts` directly — a type
 * re-export is a line of code, and this file is already at its size limit. */
export { LOW_STOCK_THRESHOLD_UNITS, parseQuantity } from '@/modules/dashboard/services/summaries';

/* ------------------------------------------------------------------------------------------------
 * The reads.
 * ---------------------------------------------------------------------------------------------- */

export const dashboardApi = {
  /**
   * The tenant's SETUP COMPLETENESS — the platform's own readiness projection.
   *
   * The dashboard does not re-derive setup completeness from the tenant's records: the platform
   * already states it, per category, with the reason for anything it does not support. A
   * dashboard that counted its own setup checklist would answer a different question from the
   * one the platform answers, and the two would disagree.
   */
  async onboarding() {
    const response = await http.get(
      '/tenants/current/onboarding',
      OnboardingReadinessResponseSchema,
    );
    return response.data;
  },

  /** The tenant's sellable offers, counted by state. */
  async offers(): Promise<OffersSummary> {
    const offers = await readOffers();
    const byState = emptyOfferStates();

    for (const offer of offers) {
      if (offer.state in byState) {
        byState[offer.state as keyof OfferStateCounts] += 1;
      }
    }

    return {
      total: offers.length,
      byState,
      needsAttention: byState.draft + byState.review,
    };
  },

  /** The canonical catalogue, counted by publication state. */
  async catalogue(): Promise<CatalogueSummary> {
    const response = await http.get(
      '/catalog/canonical-products',
      CanonicalProductListResponseSchema,
    );

    const byState: Record<string, number> = {};
    for (const product of response.data) {
      byState[product.publicationState] = (byState[product.publicationState] ?? 0) + 1;
    }

    return {
      total: response.data.length,
      published: byState['published'] ?? 0,
      byState,
      // The products are NAMED, not only counted: the read carried the names, and a
      // catalogue summary that cannot name the catalogue is a number with nothing to
      // attach it to.
      products: response.data.map((product) => ({
        id: product.id,
        name: product.name,
        modelCode: product.modelCode,
        state: product.publicationState,
      })),
    };
  },

  /**
   * The tenant's stock, counted.
   *
   * `LOW_STOCK_THRESHOLD` is a CONSOLE policy and is CARRIED on the result, so the widget
   * prints the rule that produced the low-stock count rather than presenting it as a platform
   * fact. `includeSkus` asks for the low-stock lines to be NAMED, which needs the offer read.
   */
  async stock(options: StockOptions = {}): Promise<StockSummary> {
    return readStock(options);
  },

  /** The tenant's stock movements, newest first. */
  async stockMovements(): Promise<RecentActivitySummary> {
    const response = await http.get(
      '/inventory/stock-movements',
      StockMovementListResponseSchema,
    );

    const movements = [...response.data]
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
      .map((movement) => ({
        id: movement.id,
        movementType: movement.movementType,
        quantity: parseQuantity(movement.quantity),
        unitOfMeasure: movement.unitOfMeasure,
        reason: movement.reason,
        createdAt: movement.createdAt,
      }));

    return { total: movements.length, movements };
  },

  /** The tenant's media assets, counted by status. */
  async media(): Promise<MediaSummary> {
    const response = await http.get('/content/media-assets', MediaAssetListResponseSchema);

    const byStatus: Record<string, number> = {};
    for (const asset of response.data) {
      byStatus[asset.status] = (byStatus[asset.status] ?? 0) + 1;
    }

    const needsAttention =
      (byStatus['scan_failed'] ?? 0) +
      (byStatus['upload_expired'] ?? 0) +
      (byStatus['quarantined'] ?? 0);

    return { total: response.data.length, byStatus, needsAttention };
  },

  /** The tenant's team. */
  async staff(): Promise<TeamSummary> {
    const response = await http.get(
      '/workforce/staff-memberships',
      StaffMembershipListResponseSchema,
    );

    let active = 0;
    let offboarded = 0;
    const members = response.data.map((membership) => {
      if (membership.status === 'active') active += 1;
      else offboarded += 1;
      return {
        id: membership.id,
        status: membership.status,
        functionCodes: membership.functionCodes,
        skillCodes: membership.skillCodes,
      };
    });

    return { total: members.length, active, offboarded, members };
  },

  /** The entitlements the tenant holds. */
  async entitlements(): Promise<EntitlementSummary> {
    const response = await http.get('/foundation/entitlements', EntitlementsResponseSchema);

    let usable = 0;
    const entitlements = response.data.map((entitlement) => {
      if (entitlement.usable) usable += 1;
      return {
        capability: entitlement.capability,
        scopeType: entitlement.scope.type,
        state: entitlement.state,
        usable: entitlement.usable,
      };
    });

    return { total: entitlements.length, usable, entitlements };
  },

  /**
   * The PLATFORM's tenant registry.
   *
   * A PLATFORM-scoped read: it needs the `tenant.read` platform grant, and a tenant member
   * receives `403`. The dashboard renders this widget only for an actor who holds it.
   */
  async tenants(): Promise<TenantSummary> {
    const response = await http.get('/tenants', TenantListResponseSchema);

    const byStatus: Record<string, number> = {};
    let withoutLegalEntity = 0;
    const tenants = response.data.tenants.map((tenant) => {
      byStatus[tenant.status] = (byStatus[tenant.status] ?? 0) + 1;
      if (tenant.legalEntityId === null) withoutLegalEntity += 1;
      return {
        id: tenant.id,
        slug: tenant.slug,
        status: tenant.status,
        legalEntityId: tenant.legalEntityId,
        createdAt: tenant.createdAt,
      };
    });

    return { total: tenants.length, byStatus, withoutLegalEntity, tenants };
  },

  /**
   * The platform's OPERATIONS CONTROL CENTRE backlog.
   *
   * M63's four queue depths. A PLATFORM-scoped read (`operations.backlog.read`). The read is
   * made under an audited purpose — the API refuses it without one.
   */
  async operationsBacklog() {
    const response = await http.get('/operations/backlog', OperationsBacklogResponseSchema, {
      query: { purpose: OPERATIONS_PURPOSE },
    });
    return response.data;
  },

  /**
   * The operations projection for ONE affected tenant (VND-60).
   *
   * A PLATFORM-scoped read (`operations.vendor.summary.read`). The window is the one the
   * dashboard asks for; the API states the window it actually answered with.
   */
  async vendorOperationsSummary(tenantId: string, windowFrom: string, windowTo: string) {
    const response = await http.get(
      `/operations/vendors/${tenantId}/summary`,
      VendorOperationsSummaryResponseSchema,
      {
        query: {
          purpose: OPERATIONS_PURPOSE,
          windowFrom,
          windowTo,
        },
      },
    );
    return response.data;
  },
};
